/**
 * Audio Player Store
 *
 * Global Zustand store for managing audio playback state across the application.
 * This store enables the MiniPlayer component to persist across page navigation.
 *
 * Key features:
 * - Global playback state (current track, playlist, play/pause)
 * - Time tracking (currentTime, duration)
 * - Playlist navigation (next/previous)
 * - Singleton pattern for audio element management
 */

import { create } from 'zustand';
import type { ContentAudioFile } from '@/types/content';

// =============================================================================
// Types
// =============================================================================

export interface AudioPlayerState {
  /** Currently playing track */
  currentTrack: ContentAudioFile | null;
  /** Full playlist of tracks */
  playlist: ContentAudioFile[];
  /** Title of the book being played */
  bookTitle: string | null;

  /** Whether audio is currently playing */
  isPlaying: boolean;
  /** Current playback position in seconds */
  currentTime: number;
  /** Total duration of current track in seconds */
  duration: number;
}

export interface AudioPlayerActions {
  /**
   * Start playing a track from a playlist
   * @param track - The track to play
   * @param playlist - The full playlist of tracks
   * @param bookTitle - The title of the book
   */
  play: (track: ContentAudioFile, playlist: ContentAudioFile[], bookTitle: string) => void;

  /** Pause the current playback */
  pause: () => void;

  /** Resume playback after pausing */
  resume: () => void;

  /** Skip to the next track in the playlist */
  next: () => void;

  /** Go back to the previous track in the playlist */
  previous: () => void;

  /**
   * Seek to a specific time in the current track
   * @param time - Time in seconds to seek to
   */
  seek: (time: number) => void;

  /** Close the player and reset all state */
  close: () => void;

  /**
   * Update the current playback time (called from timeupdate event)
   * @param time - Current time in seconds
   */
  setCurrentTime: (time: number) => void;

  /**
   * Update the duration (called when metadata loads)
   * @param duration - Duration in seconds
   */
  setDuration: (duration: number) => void;
}

export type AudioPlayerStore = AudioPlayerState & AudioPlayerActions;

// =============================================================================
// Initial State
// =============================================================================

const initialState: AudioPlayerState = {
  currentTrack: null,
  playlist: [],
  bookTitle: null,
  isPlaying: false,
  currentTime: 0,
  duration: 0,
};

// =============================================================================
// Store
// =============================================================================

/**
 * Global audio player store
 *
 * Usage:
 * ```tsx
 * // Access state and actions
 * const { currentTrack, isPlaying, play, pause, next, previous, close } = useAudioPlayerStore();
 *
 * // Start playback
 * play(audioFile, playlist, bookTitle);
 *
 * // Toggle play/pause
 * isPlaying ? pause() : resume();
 * ```
 */
export const useAudioPlayerStore = create<AudioPlayerStore>((set, get) => ({
  // Initial state
  ...initialState,

  // Actions
  play: (track, playlist, bookTitle) =>
    set({
      currentTrack: track,
      playlist,
      bookTitle,
      isPlaying: true,
      currentTime: 0,
      duration: track.durationSeconds || 0,
    }),

  pause: () => set({ isPlaying: false }),

  resume: () => {
    const { currentTrack } = get();
    if (currentTrack) {
      set({ isPlaying: true });
    }
  },

  next: () => {
    const { playlist, currentTrack } = get();
    if (!currentTrack || playlist.length === 0) return;

    const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
    if (currentIndex < playlist.length - 1) {
      const nextTrack = playlist[currentIndex + 1];
      set({
        currentTrack: nextTrack,
        currentTime: 0,
        duration: nextTrack.durationSeconds || 0,
      });
    }
  },

  previous: () => {
    const { playlist, currentTrack, currentTime } = get();
    if (!currentTrack || playlist.length === 0) return;

    // If more than 3 seconds into track, restart current track
    if (currentTime > 3) {
      set({ currentTime: 0 });
      return;
    }

    const currentIndex = playlist.findIndex((t) => t.id === currentTrack.id);
    if (currentIndex > 0) {
      const prevTrack = playlist[currentIndex - 1];
      set({
        currentTrack: prevTrack,
        currentTime: 0,
        duration: prevTrack.durationSeconds || 0,
      });
    }
  },

  seek: (time) => set({ currentTime: time }),

  close: () => set(initialState),

  setCurrentTime: (time) => set({ currentTime: time }),

  setDuration: (duration) => set({ duration }),
}));

// =============================================================================
// Selectors
// =============================================================================

/**
 * Selector to check if player is visible (has a current track)
 */
export const selectIsPlayerVisible = (state: AudioPlayerStore): boolean =>
  state.currentTrack !== null;

/**
 * Selector to get current track index in playlist
 */
export const selectCurrentTrackIndex = (state: AudioPlayerStore): number => {
  if (!state.currentTrack || state.playlist.length === 0) return -1;
  return state.playlist.findIndex((t) => t.id === state.currentTrack?.id);
};

/**
 * Selector to check if there is a next track
 */
export const selectHasNextTrack = (state: AudioPlayerStore): boolean => {
  const index = selectCurrentTrackIndex(state);
  return index >= 0 && index < state.playlist.length - 1;
};

/**
 * Selector to check if there is a previous track
 */
export const selectHasPreviousTrack = (state: AudioPlayerStore): boolean => {
  const index = selectCurrentTrackIndex(state);
  return index > 0;
};

/**
 * Selector to get progress percentage (0-100)
 */
export const selectProgressPercent = (state: AudioPlayerStore): number => {
  if (state.duration <= 0) return 0;
  return (state.currentTime / state.duration) * 100;
};
